import { useEffect, useRef, type ReactNode } from 'react';

interface FadeInProps {
  children: ReactNode;
  threshold?: number;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  delay?: number;
}

/**
 * IntersectionObserver-driven fade-in. CSS class `.fade-in` is in globals.css.
 * Reduced motion: opacity transitions stay but transforms are zeroed out.
 */
export function FadeIn({
  children,
  threshold = 0.18,
  as = 'div',
  className,
  delay = 0,
}: FadeInProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setTimeout(() => el.setAttribute('data-in', 'true'), delay);
            obs.unobserve(entry.target);
          }
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, delay]);

  const Tag = as as 'div';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const RefForward: any = ref;

  return (
    <Tag ref={RefForward} className={`fade-in ${className ?? ''}`}>
      {children}
    </Tag>
  );
}
